import { useLogger } from "@bindery/logging/react";
import { Button } from "@bindery/ui/components/ui/button";
import { cn } from "@bindery/ui/lib/utils";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { useEffect, useState, useSyncExternalStore } from "react";
import { useLocation, useNavigate } from "react-router";
import { saveProgressHref } from "@/auth/links";
import {
  readerIdOf,
  type SessionStore,
  sessionStore,
  useSession,
} from "@/convex/session";
import {
  type Milestone,
  MILESTONE_TOAST_MS,
  milestoneMessage,
  readMilestones,
  withoutPending,
  writeMilestones,
} from "./milestone";
import { isUnconfirmed } from "@/storage/unconfirmed-reader";
import { linkedOf } from "./session-view";

// ../app showed it on returning home (home-client.tsx:30,48); the reading
// page's back link lands on the library, so it shows there too.
const TOAST_PATHS: ReadonlySet<string> = new Set(["/", "/reader"]);

interface Shown {
  threshold: Milestone;
  words: number;
}

// The pending flag lives in localStorage, so taking it is an effect; the
// toast it raises is this small store's, not React state set from that effect.
function createToastSlot() {
  let shown: Shown | null = null;
  const listeners = new Set<() => void>();
  const set = (next: Shown | null) => {
    shown = next;
    for (const listener of listeners) listener();
  };
  return {
    get: () => shown,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    show: (next: Shown) => set(next),
    dismiss: () => set(null),
  };
}

/**
 * Takes a reader's pending milestone once, and only while no account is
 * linked to the reader: a link known to exist drops it unshown, and an
 * unknown answer leaves it waiting. So does a scanned reader nobody has
 * confirmed yet, whose identity gate is on screen.
 */
function useTakePendingMilestone(
  store: SessionStore,
  show: (shown: Shown) => void,
): void {
  const session = useSession(store);
  const readerId = readerIdOf(session);
  const linked = linkedOf(session);
  const { pathname, key } = useLocation();
  const onToastPath = TOAST_PATHS.has(pathname);

  // `key` re-runs this after "That's me", which clears the marker and
  // replaces the location.
  useEffect(() => {
    if (readerId === null || linked === null || !onToastPath) return;
    if (isUnconfirmed(readerId)) return;
    const state = readMilestones(readerId);
    if (state.pending === null) return;
    writeMilestones(readerId, withoutPending(state));
    if (linked === false) show(state.pending);
  }, [readerId, linked, onToastPath, key, show]);
}

export function MilestoneToast({
  store = sessionStore,
  durationMs = MILESTONE_TOAST_MS,
}: {
  store?: SessionStore;
  durationMs?: number;
}) {
  const [slot] = useState(createToastSlot);
  const shown = useSyncExternalStore(slot.subscribe, slot.get, () => null);
  useTakePendingMilestone(store, slot.show);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const log = useLogger();

  // Each holds the toast it was set for, so a pause cannot leak into the
  // next toast when this one unmounts under the pointer or focus.
  const [hoveredFor, setHoveredFor] = useState<Shown | null>(null);
  const [focusedFor, setFocusedFor] = useState<Shown | null>(null);
  const paused =
    shown !== null && (hoveredFor === shown || focusedFor === shown);

  useEffect(() => {
    if (shown === null || paused) return;
    const timer = setTimeout(slot.dismiss, durationMs);
    return () => clearTimeout(timer);
  }, [shown, paused, slot, durationMs]);

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-[80] flex justify-center px-4">
      <div
        onMouseEnter={() => setHoveredFor(shown)}
        onMouseLeave={() => setHoveredFor(null)}
        onFocus={() => setFocusedFor(shown)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget))
            setFocusedFor(null);
        }}
        className={cn(
          shown !== null &&
            "bg-popover text-popover-foreground motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-xl border p-4 text-sm shadow-lg",
        )}
      >
        <output
          aria-live="polite"
          className={shown === null ? "sr-only" : "flex-1"}
        >
          {shown === null ? "" : milestoneMessage(shown.threshold, shown.words)}
        </output>
        {shown !== null && (
          <>
            <Button
              size="sm"
              onClick={() => {
                log.event("auth.redirect.initiated", {
                  source: "milestone_toast",
                  milestone: shown.threshold,
                });
                slot.dismiss();
                void navigate(saveProgressHref(pathname));
              }}
            >
              Sign in
            </Button>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={slot.dismiss}
              className="text-muted-foreground hover:text-foreground rounded-md p-1"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
